import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, asset } from "../api";
import { Layout } from "../components/Layout";
import { useReveal, gsap, ScrollTrigger } from "../useReveal";

export default function Home() {
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data,
  });
  const ready = !isLoading && projects.length > 0;
  useReveal(ready);

  const flagship = projects.find((p) => p.slug === "ozalp-park-konutlari") || projects.find((p) => p.status === "tamamlandi");
  const devamEden = projects.filter((p) => p.status === "devam-eden").length;
  const tamamlanan = projects.filter((p) => p.status === "tamamlandi").length;
  const serit = useRef(null);

  useEffect(() => {
    if (!ready || window.innerWidth <= 860) return;
    const el = serit.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.to(el, {
        x: () => -(el.scrollWidth - window.innerWidth + 60),
        ease: "none",
        scrollTrigger: { trigger: "#serit-sarici", start: "top top", end: "bottom bottom", scrub: 0.6, invalidateOnRefresh: true },
      });
    });
    const t = setTimeout(() => ScrollTrigger.refresh(), 200);
    return () => { clearTimeout(t); ctx.revert(); };
  }, [ready]);

  return (
    <>
      <Layout transparan mesaj="Projeleriniz hakkında bilgi almak istiyorum">
        {/* HERO */}
        <div className="hero" data-testid="hero">
          {flagship && <img src={asset(flagship.hero_gunduz)} alt="Özalp Park Konutları, Kartal" />}
          {flagship && <img className="heroGece" src={asset(flagship.hero_gece || flagship.hero_gunduz)} alt="Akşam görünümü" />}
          <div className="hero-ic">
            <div className="veri">Kartal, İstanbul — Teslim edildi</div>
            <h1 className="h-dev">Bitirdiğimiz her bina, bir sonrakinin sözüdür</h1>
            <div className="hero-alt">
              <p>Özalp Park Konutları'nda anahtarlar teslim edildi, havuz doldu, çocuklar bahçeye indi. Şimdi Kadıköy'deyiz.</p>
              <Link to="/projeler" className="dugme" data-testid="hero-projeler-btn">Projeleri gör</Link>
            </div>
          </div>
        </div>

        {/* MANİFESTO */}
        <section className="manifesto">
          <p className="bel">İstanbul'un bu yakasında az sayıda iş yapıyoruz. <em>Sebebi basit:</em> her birini bitirmek istiyoruz.</p>
        </section>

        {/* RAKAMLAR */}
        <section style={{ paddingTop: 0 }}>
          <div className="rakamlar bel">
            <div><b>{devamEden}</b><span className="veri">Devam eden proje</span></div>
            <div><b>{tamamlanan}</b><span className="veri">Tamamlanan proje</span></div>
            <div><b>{flagship?.kunye?.blok?.replace(/\D/g, "") || "2"}</b><span className="veri">Blok · Özalp Park</span></div>
            <div><b>{flagship?.kunye?.kat?.replace(/\D/g, "") || "12"}</b><span className="veri">Kat</span></div>
          </div>
        </section>

        {/* PROJELER YATAY */}
        <section className="projeler-serit" id="projeler">
          <div className="bolum-basi">
            <h2 className="h-orta bel">Projeler</h2>
            <div className="veri bel">Tamamlanan {tamamlanan} · Devam eden {devamEden}</div>
          </div>
        </section>
        <div id="serit-sarici">
          <div id="serit-yapisan">
            <div id="serit" ref={serit}>
              {projects.map((p) => (
                <Link to={`/proje/${p.slug}`} className="proje" key={p.id} data-testid={`home-proje-${p.slug}`}>
                  <div className="kutu">
                    <span className={`rozet ${p.status === "devam-eden" ? "aktif" : ""}`}>
                      {p.status === "devam-eden" ? "Devam ediyor" : p.status === "yakinda" ? "Yakında" : "Tamamlandı"}
                    </span>
                    <img src={asset(p.hero_gunduz)} alt={p.name} />
                  </div>
                  <h3>{p.name}</h3>
                  <div className="veri">{(p.summary || "").split("·").slice(0, 3).map((s) => s.trim()).join(" · ")}</div>
                </Link>
              ))}
            </div>
            <div className="serit-ipucu veri">← Yana kaydırın</div>
          </div>
        </div>

        {/* VİTRİN */}
        {flagship && (
          <section id="vitrin">
            <div className="bolum-basi">
              <h2 className="h-orta bel">{flagship.name}</h2>
              <div className="veri bel">{flagship.location} · Teslim edildi</div>
            </div>
            <div className="vitrin">
              <figure className="v-a bel"><img src={asset(flagship.gallery?.[0])} alt="Havuz ve cephe" /></figure>
              <figure className="v-b bel"><img src={asset(flagship.gallery?.[1])} alt="Bina cephesi" /></figure>
              <figure className="v-c bel"><img src={asset(flagship.gallery?.[2])} alt="Avlu görünümü" /></figure>
            </div>
            <div className="olanaklar bel">
              {(flagship.olanaklar || []).map((o) => (
                <div key={o.no}><span>{o.no}</span>{o.text}</div>
              ))}
            </div>
          </section>
        )}

        {/* KONUM */}
        {flagship && (
          <section id="konum">
            <div className="konum">
              <figure className="bel"><img src={asset(flagship.gallery?.[3] || flagship.hero_gunduz)} alt="Konum" /></figure>
              <div className="bel">
                <div className="veri">Konum</div>
                <h2 className="h-orta" style={{ margin: "12px 0 22px" }}>{flagship.location.split(",")[0]},<br />Anadolu Yakası</h2>
                {(flagship.distances || []).map((d) => (
                  <div className="mesafe" key={d.label}><span>{d.label}</span><b>{d.value}</b></div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* KAPANIŞ */}
        <section className="kapanis" id="iletisim">
          <h2 className="h-orta bel">Kadıköy projesini yerinde görün</h2>
          <p className="bel">Satış ofisimizde randevu oluşturun, kat planlarını ve ödeme seçeneklerini birlikte inceleyelim.</p>
          <Link to="/randevu" className="dugme bel" data-testid="home-randevu-btn">Randevu oluştur</Link>
        </section>
      </Layout>
    </>
  );
}
